function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isHtmlContent(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }
  // Only treat as HTML if the text starts with a known block-level or
  // formatting HTML tag. This prevents false positives from medical text
  // that contains angle brackets (e.g., "<normal", "<unchanged").
  return /^\s*<\s*(?:p|div|span|strong|em|ul|ol|li|hr|br|h[1-6]|a|table|tr|td|th|tbody|thead|blockquote|pre|code|img|figure|figcaption|section|article|header|footer|dl|dt|dd)\b/i.test(
    trimmed
  );
}

function formatInlineMarkdown(text: string): string {
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  return segments
    .map((segment) => {
      const boldMatch = /^\*\*([^*]+)\*\*$/.exec(segment);
      if (boldMatch) {
        return `<strong>${escapeHtml(boldMatch[1])}</strong>`;
      }
      return escapeHtml(segment);
    })
    .join("");
}

type ListKind = "ul" | "ol";

function stripBulletPrefix(line: string): string {
  const trimmed = line.trim();
  if (trimmed.startsWith("•")) {
    return trimmed.slice(1).trim();
  }
  if (trimmed.startsWith("o ")) {
    return trimmed.slice(2).trim();
  }
  if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
    return trimmed.slice(2).trim();
  }
  if (trimmed.startsWith("-") || trimmed.startsWith("*")) {
    return trimmed.slice(1).trim();
  }
  return trimmed;
}

function stripOrderedPrefix(line: string): string {
  const trimmed = line.trim();
  const match = /^(\d+)\.\s*(.*)$/.exec(trimmed);
  if (match) {
    return match[2].trim();
  }
  return trimmed;
}

function isBulletLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed === "---" || trimmed === "***") {
    return false;
  }
  if (trimmed.startsWith("•")) {
    return true;
  }
  if (trimmed.startsWith("o ")) {
    return true;
  }
  if (/^[-*]\s/.test(trimmed)) {
    return true;
  }
  return false;
}

function isOrderedLine(line: string): boolean {
  return /^\d+\.\s/.test(line.trim());
}

function flushList(items: string[], kind: ListKind): string {
  const tag = kind;
  const listItems = items
    .map((item) => `<li>${formatInlineMarkdown(item)}</li>`)
    .join("");
  return `<${tag}>${listItems}</${tag}>`;
}

function summaryContentToHtml(text: string | null | undefined): string {
  if (!text) {
    return "";
  }

  const trimmed = text.trim();
  if (!trimmed) {
    return "";
  }

  if (isHtmlContent(trimmed)) {
    return trimmed;
  }

  const lines = text.split(/\r?\n/);
  const blocks: string[] = [];
  let bulletBuffer: string[] = [];
  let orderedBuffer: string[] = [];

  const flushBulletBuffer = () => {
    if (bulletBuffer.length > 0) {
      blocks.push(flushList(bulletBuffer, "ul"));
      bulletBuffer = [];
    }
  };

  const flushOrderedBuffer = () => {
    if (orderedBuffer.length > 0) {
      blocks.push(flushList(orderedBuffer, "ol"));
      orderedBuffer = [];
    }
  };

  const flushAllLists = () => {
    flushBulletBuffer();
    flushOrderedBuffer();
  };

  for (const rawLine of lines) {
    const line = rawLine;
    const lineTrimmed = line.trim();

    if (lineTrimmed === "---" || lineTrimmed === "***") {
      flushAllLists();
      blocks.push("<hr>");
      continue;
    }

    if (!lineTrimmed) {
      flushAllLists();
      continue;
    }

    if (isBulletLine(line)) {
      flushOrderedBuffer();
      bulletBuffer.push(stripBulletPrefix(line));
      continue;
    }

    if (isOrderedLine(line)) {
      flushBulletBuffer();
      orderedBuffer.push(stripOrderedPrefix(line));
      continue;
    }

    flushAllLists();
    blocks.push(`<p>${formatInlineMarkdown(lineTrimmed)}</p>`);
  }

  flushAllLists();

  return blocks.join("");
}

function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html || !html.trim()) {
    return true;
  }

  const withoutBreaks = html
    .replace(/<p><br><\/p>/gi, "")
    .replace(/<p>\s*<\/p>/gi, "")
    .trim();

  if (!withoutBreaks) {
    return true;
  }

  const textOnly = withoutBreaks.replace(/<[^>]+>/g, "").trim();
  return textOnly.length === 0;
}

export {
  escapeHtml,
  formatInlineMarkdown,
  isHtmlContent,
  isRichTextEmpty,
  summaryContentToHtml,
};
